import { useEffect, useMemo, useState } from "react";
import { Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { trpc } from "@/lib/trpc";
import {
  DEFAULT_AREA_COLOR,
  type ListColorKey,
  resolveTaskColors,
} from "@/lib/listColors";
import TaskDetailsHeader from "@/components/tasks/details/TaskDetailsHeader/TaskDetailsHeader";
import TaskInfoSection from "@/components/tasks/details/TaskInfoSection/TaskInfoSection";
import TaskFieldsForm, {
  type TaskFieldsValue,
} from "@/components/tasks/details/TaskFieldsForm/TaskFieldsForm";
import PatternScheduleForm from "@/components/tasks/patterns/PatternScheduleForm/PatternScheduleForm";
import ListSelector, {
  type ListOption,
} from "@/components/foundations/lists/ListSelector/ListSelector";
import PatternSupportPreview from "@/components/tasks/options/PatternSupportPreview/PatternSupportPreview";
import PatternOptionsView from "@/components/tasks/options/PatternOptionsView/PatternOptionsView";
import PatternDisplayView from "@/components/tasks/options/PatternDisplayView/PatternDisplayView";
import {
  DEFAULT_TASK_VISIBLE_FIELDS,
  normalizeTaskVisibleFields,
  type TaskVisibleFields,
} from "@shared/taskVisibleFields";
import { toast } from "sonner";

type PatternTaskSource = {
  id: number;
  title: string;
  details?: string | null;
  listId?: number | null;
  startMinutes?: number | null;
  durationMin?: number | null;
  alertEnabled?: boolean;
  barColor?: string | null;
  bgColor?: string | null;
  visibleFields?: TaskVisibleFields | null;
};

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  templateId?: number | null;
  listId?: number | null;
  task?: PatternTaskSource;
  onSaved?: () => void;
};

type Repeats = "endTime" | "count";

const emptyForm: TaskFieldsValue = {
  title: "",
  details: "",
  startTime: "",
  durationMin: "",
  alertEnabled: true,
  priority: null,
  repeat: {
    enabled: false,
    intervalMin: "60",
    endType: "endTime",
    endTime: "",
    count: "",
  },
  customColors: false,
  barColor: null,
  bgColor: null,
};

function minutesToTime(value?: number | null) {
  if (value === null || value === undefined) return "";
  const hours = Math.floor(value / 60) % 24;
  const minutes = value % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
}

export default function PatternEditorModal({
  open,
  onOpenChange,
  templateId,
  listId: initialListId,
  task,
  onSaved,
}: Props) {
  const utils = trpc.useUtils();
  const listsQuery = trpc.lists.list.useQuery(undefined, { enabled: open });
  const templatesQuery = trpc.templates.list.useQuery(
    { listId: initialListId ?? task?.listId ?? 0 },
    {
      enabled:
        open && Boolean(templateId) && Boolean(initialListId ?? task?.listId),
    }
  );
  const template = templatesQuery.data?.find(item => item.id === templateId);
  const [listId, setListId] = useState(initialListId ?? task?.listId ?? 0);
  const [listPickerOpen, setListPickerOpen] = useState(false);
  const [form, setForm] = useState<TaskFieldsValue>(emptyForm);
  const [visibleFields, setVisibleFields] = useState<TaskVisibleFields>(
    DEFAULT_TASK_VISIBLE_FIELDS
  );
  const [initializedFor, setInitializedFor] = useState<string | null>(null);
  const [view, setView] = useState<"main" | "options" | "display">("main");

  const sourceKey = `${templateId ?? "new"}:${task?.id ?? "none"}`;
  useEffect(() => {
    if (!open || initializedFor === sourceKey) return;
    if (templateId && !template) return;
    const source = template ?? task;
    const bar = (source?.barColor ?? null) as ListColorKey | null;
    const bg = (source?.bgColor ?? null) as ListColorKey | null;
    setListId(source?.listId ?? initialListId ?? 0);
    setView("main");
    setForm({
      title: source?.title ?? "",
      details: source?.details ?? "",
      startTime: template
        ? (template.defaultStartTime ?? "")
        : minutesToTime(task?.startMinutes),
      durationMin: template
        ? (template.defaultDurationMin?.toString() ?? "")
        : (task?.durationMin?.toString() ?? ""),
      alertEnabled: template
        ? template.defaultAlertEnabled
        : (task?.alertEnabled ?? true),
      priority: null,
      repeat: {
        enabled: template?.defaultRepeatEnabled ?? false,
        intervalMin: template?.defaultRepeatIntervalMin?.toString() ?? "60",
        endType: (template?.defaultRepeatEndType as Repeats) ?? "endTime",
        endTime: template?.defaultRepeatEndTime ?? "",
        count: template?.defaultRepeatCount?.toString() ?? "",
      },
      customColors: bar !== null || bg !== null,
      barColor: bar,
      bgColor: bg,
    });
    setVisibleFields(
      normalizeTaskVisibleFields(
        template?.defaultVisibleFields ?? task?.visibleFields
      )
    );
    setInitializedFor(sourceKey);
  }, [
    open,
    sourceKey,
    templateId,
    template,
    task,
    initialListId,
    initializedFor,
  ]);

  useEffect(() => {
    if (!open) setInitializedFor(null);
  }, [open]);

  const selectedArea = (listsQuery.data ?? []).find(item => item.id === listId);
  const colors = resolveTaskColors({
    barColor: form.customColors ? form.barColor : null,
    bgColor: form.customColors ? form.bgColor : null,
    listColor: selectedArea?.color ?? DEFAULT_AREA_COLOR,
    listBgColor: selectedArea?.bgColor ?? null,
  });

  const createTemplate = trpc.templates.create.useMutation({
    onSuccess: () => {
      void utils.templates.list.invalidate();
      void utils.lists.list.invalidate();
      toast.success("Pattern created");
      onSaved?.();
      onOpenChange(false);
    },
    onError: error => toast.error(error.message),
  });
  const updateTemplate = trpc.templates.update.useMutation({
    onSuccess: () => {
      void utils.templates.list.invalidate();
      void utils.lists.list.invalidate();
      toast.success("Pattern updated");
      onSaved?.();
      onOpenChange(false);
    },
    onError: error => toast.error(error.message),
  });

  const payload = useMemo(() => {
    const intInput = (value: string) =>
      value.trim() === "" ? null : Math.max(1, parseInt(value, 10) || 1);
    const repeat = form.repeat!;
    return {
      title: form.title.trim(),
      details: form.details.trim() || undefined,
      defaultStartTime: form.startTime || null,
      defaultDurationMin: intInput(form.durationMin),
      defaultRepeatEnabled: repeat.enabled,
      defaultRepeatIntervalMin: repeat.enabled
        ? intInput(repeat.intervalMin)
        : null,
      defaultRepeatEndType: repeat.enabled ? repeat.endType : null,
      defaultRepeatEndTime:
        repeat.enabled && repeat.endType === "endTime"
          ? repeat.endTime || null
          : null,
      defaultRepeatCount:
        repeat.enabled && repeat.endType === "count"
          ? intInput(repeat.count)
          : null,
      defaultAlertEnabled: form.alertEnabled,
      defaultVisibleFields: visibleFields,
      barColor: form.customColors ? form.barColor : null,
      bgColor: form.customColors ? form.bgColor : null,
    };
  }, [form, visibleFields]);

  const save = () => {
    if (!listId || !payload.title) {
      toast.error("A list and title are required");
      return;
    }
    if (templateId) {
      updateTemplate.mutate({ id: templateId, listId, ...payload });
    } else {
      createTemplate.mutate({ listId, ...payload });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[90vh] max-w-lg flex-col overflow-hidden">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {templateId ? "Edit pattern" : "New pattern"}
          </DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-x-hidden overflow-y-auto">
          <div
            className="flex w-[300%] transition-transform duration-300 ease-[cubic-bezier(0.23,1,0.32,1)]"
            style={{
              transform:
                view === "main"
                  ? "translateX(0%)"
                  : view === "options"
                    ? "translateX(-33.333333%)"
                    : "translateX(-66.666667%)",
            }}
          >
            <div className="w-1/3 shrink-0 space-y-3 py-2">
              <TaskDetailsHeader
                listName={selectedArea?.name ?? "List"}
                colors={colors}
                onAreaClick={() => setListPickerOpen(true)}
                onOptionsClick={() => setView("options")}
              />
              <TaskInfoSection
                value={{ title: form.title, description: form.details }}
                savedValue={{ title: form.title, description: form.details }}
                colors={colors}
                onChange={next =>
                  setForm(current => ({
                    ...current,
                    title: next.title,
                    details: next.description,
                  }))
                }
                onCommit={() => undefined}
                onDisplayClick={() => setView("display")}
              />
              <PatternScheduleForm
                value={{
                  startTime: form.startTime,
                  durationMinutes: form.durationMin,
                  alertEnabled: form.alertEnabled,
                }}
                onChange={next =>
                  setForm(current => ({
                    ...current,
                    startTime: next.startTime,
                    durationMin: next.durationMinutes,
                    alertEnabled: next.alertEnabled,
                  }))
                }
                onCommit={next =>
                  setForm(current => ({
                    ...current,
                    durationMin: next.durationMinutes,
                  }))
                }
                open
                alwaysOpen
                onOpenChange={() => undefined}
                colors={colors}
                emptyLabel="Default schedule"
              />
              <TaskFieldsForm
                value={form}
                onChange={setForm}
                onCommit={partial => {
                  if (partial.repeat) {
                    setForm(current => ({
                      ...current,
                      repeat: { ...current.repeat!, ...partial.repeat },
                    }));
                  }
                }}
                sections={["repeat"]}
                labels={{
                  previewLabel: selectedArea?.name ?? "List",
                  fallbackBar: selectedArea?.color ?? DEFAULT_AREA_COLOR,
                  fallbackBg: selectedArea?.bgColor ?? null,
                }}
                accent={{ bar: colors.bar, soft: colors.soft, ink: colors.ink }}
              />
              <PatternSupportPreview
                visibleFields={visibleFields}
                colors={colors}
              />
              <div className="flex justify-end gap-2 px-1 pt-3">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={save}
                  disabled={
                    createTemplate.isPending || updateTemplate.isPending
                  }
                >
                  <Save className="mr-2 h-4 w-4" /> Save pattern
                </Button>
              </div>
            </div>
            <div className="w-1/3 shrink-0 py-2">
              <PatternOptionsView
                title={form.title}
                details={form.details}
                listName={selectedArea?.name ?? "List"}
                colors={colors}
                visibleFields={visibleFields}
                repeatRule={
                  form.repeat?.enabled
                    ? {
                        kind: "interval",
                        unit: "minute",
                        every: Number(form.repeat.intervalMin || 60),
                        end: { kind: "count", count: 1 },
                      }
                    : { kind: "none" }
                }
                onToggleVisibility={field =>
                  setVisibleFields(current => ({
                    ...current,
                    [field]: !current[field],
                  }))
                }
                onOpenDisplay={() => setView("display")}
                onClone={() => setView("main")}
                onDelete={() => onOpenChange(false)}
                onBack={() => setView("main")}
              />
            </div>
            <div className="w-1/3 shrink-0 py-2">
              <PatternDisplayView
                title={form.title}
                listName={selectedArea?.name ?? "List"}
                colors={colors}
                value={{
                  inheritView: !form.customColors,
                  barColor: form.barColor,
                  bgColor: form.bgColor,
                }}
                fallbackBar={selectedArea?.color ?? DEFAULT_AREA_COLOR}
                fallbackBg={selectedArea?.bgColor ?? null}
                onChange={next =>
                  setForm(current => ({
                    ...current,
                    customColors: !next.inheritView,
                    barColor: next.barColor,
                    bgColor: next.bgColor,
                  }))
                }
                onBack={() => setView("main")}
              />
            </div>
          </div>
        </div>
        {listPickerOpen && (
          <ListSelector
            lists={(listsQuery.data ?? []) as ListOption[]}
            value={listId}
            onChange={next => {
              if (next !== null) setListId(next);
              setListPickerOpen(false);
            }}
            onClose={() => setListPickerOpen(false)}
            title="Move pattern to list"
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
